/**
 * Addon Page.
 */
import AddonClass from '@shared/classes/addon';
import Main from '@shared/types/enums/main';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import TranslationManager from '@shared/assets/locale/translationsManager';
import MainTemplate from '@components/templates/main/ui/main';
import addonObservable from '@shared/api/addons/addonObservable';
import useComplete from '@shared/components/utils/hooks/useComplete';
import { Observer } from 'rxjs';
import { appSelector } from '@shared/store/slices/app.slice';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useState } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Displays the addon page with the rails and sliders.
 */
export default function Addon() {
  // Selectors
  const { urls } = useAppSelector(appSelector);
  const { parental } = useAppSelector(userSelector);
  const currentParams = useAppSelector(selectCurrentParams);

  // State
  const [message, setMessage] = useState('');
  const [complete, setComplete] = useComplete();
  const [rails, setRails] = useState<Rail[]>(
    (currentParams as { rails: Rail[] })?.rails || []
  );
  const [sliders, setSliders] = useState<Slider[]>(
    (currentParams as { sliders: Slider[] })?.sliders || []
  );

  useEffect(() => {
    const parentalControl =
      (currentParams as { asset: AddonClass })?.asset?.parentalControl &&
      parental?.filterAdultChannels;

    if (parentalControl) {
      setMessage(
        TranslationManager.get(
          'PAGES.ADDON.PARENTAL_CONTROL',
          urls.account_link
        )
      );
      return;
    }
    if (currentParams?.rails || currentParams?.sliders) {
      return;
    }

    const addonId =
      (currentParams as { asset: Slider })?.asset?.addon ||
      (currentParams as { asset: AddonClass })?.asset?.id;
    const planId = (currentParams as { asset: AddonClass })?.asset?.planId;
    const title = (currentParams as { asset: AddonClass })?.asset?.title;

    if (addonId) {
      const addonObserver: Observer<{
        data: Slider[] | Rail[];
        type: number;
      }> = {
        next: (val) => {
          if (val.type === Main.SLIDERS) {
            setSliders(val.data as Slider[]);
          } else if (val.type === Main.RAILS) {
            setRails(val.data as Rail[]);
          }
        },
        error: () => {
          console.error('Error recovering addons');
        },
        complete: () => {
          setComplete(true);
          return null;
        },
      };
      addonObservable(addonId, planId, title).subscribe(
        addonObserver as Partial<Observer<unknown>>
      );
    }
  }, []);

  return (
    <MainTemplate
      flags={{ complete }}
      rails={rails}
      sliders={sliders}
      message={message}
    />
  );
}
